export interface ImageTool {
  title: string;
  description: string;
  uri: string;
  image: string;
  isNew?: boolean;
  isVideoPreview?: boolean;
  slug?: string;
  landingPage?: string;
  seo_title?: string;
  seo_description?: string;
  skipOnLandingPage?: boolean;
  skipStaticPaths?: boolean;
}

export const imageTools: ImageTool[] = [
  {
    title: 'Text to Image',
    description: 'Generate images from text descriptions',
    uri: '/ai-images',
    image: '/img/tools/TextToImageGraphic.jpg',
    slug: 'text-to-image',
    seo_title: 'AI Text to Image Generator - Create Images from Text',
    seo_description:
      'Transform your words into stunning visuals with our AI Text to Image Generator. Create high-quality images from text descriptions instantly.',
  },
  {
    title: 'Character Reference',
    description:
      'Generate consistent character appearances across multiple images. Maintain facial features, proportions, and distinctive traits for cohesive storytelling and branding.',
    uri: '/ai-images?action=character-reference',
    image: '/img/tools/character-reference.png',
    slug: 'character-reference',
    isNew: true,
    seo_title:
      'AI Character Reference - Create Consistent Character Appearances',
    seo_description:
      'Generate consistent character appearances across multiple images. Maintain facial features, proportions, and distinctive traits for cohesive storytelling and branding.',
  },
  {
    title: 'Turn photo into muppets',
    description: 'Use AI to turn photos into muppets',
    uri: '/ai-images?action=muppets',
    image: '/img/tools/muppets.png',
    slug: 'muppets',
    seo_title:
      'Turn Photos into Muppets with AI - Muppet Style Photo Converter',
    seo_description:
      'Transform any photo into fun muppet-style characters with our AI muppet generator. Convert portraits into muppet-like images instantly.',
  },
  {
    title: 'Ghiblify',
    description: 'Transform photos into Studio Ghibli style artwork',
    uri: '/ai-images?action=ghiblify',
    image: '/img/tools/japanese2.png',
    isNew: true,
    slug: 'ghiblify',
    seo_title: 'Ghiblify Your Photos - Studio Ghibli Style AI Image Converter',
    seo_description:
      'Transform your photos into beautiful Studio Ghibli style artwork with our AI Ghiblify tool. Create magical anime-inspired images instantly.',
  },
  {
    title: 'Creative Upscale',
    description:
      'Enhance your low resolution images to high quality while maintaining the details',
    uri: '/magic-upscaler',
    image: '/img/tools/CreativeUpscaleGraphic.jpg',
    slug: 'creative-upscale',
    seo_title: 'AI Image Upscaler - Enhance Low Resolution Images',
    seo_description:
      'Enhance low resolution images to high quality while maintaining details with our AI upscaling tool. Turn blurry photos into crystal clear images.',
    skipStaticPaths: true,
  },
  {
    title: 'Animate Image',
    description: 'Add movement and life to your static images',
    uri: '/ai-videos',
    isVideoPreview: true,
    // The previous preview lived on fal.media, whose host stopped resolving —
    // the card rendered as an empty black video everywhere it appears.
    image: 'https://media.easy-peasy.ai/landing/home/vid-butterfly.mp4',
    slug: 'animate-image',
    seo_title: 'AI Image Animation - Bring Your Photos to Life',
    seo_description:
      'Add movement and life to your static images with our AI animation tool. Transform still photos into captivating video clips instantly.',
    skipStaticPaths: true,
  },
  {
    title: 'Sketch to Image',
    description: 'Transform your sketches into real images',
    uri: '/ai-images?action=sketch-to-image',
    image: '/img/tools/SketchToImageGraphic.jpg',
    slug: 'sketch-to-image',
    seo_title: 'AI Sketch to Image - Convert Drawings to Realistic Images',
    seo_description:
      'Transform your sketches and drawings into realistic images with our AI sketch converter. Turn simple drawings into detailed visuals.',
  },
  {
    title: 'Edit Image with AI',
    description: 'Modify and enhance your images with AI assistance',
    uri: '/ai-image-editor',
    image: '/img/tools/FindReplaceGraphic.jpg',
    slug: 'ai-image-editor',
    seo_title: 'AI Image Editor - Modify and Enhance Images with AI',
    seo_description:
      'Modify and enhance your images with AI assistance. Make complex edits, add or remove objects, and transform your photos with our AI editing tools.',
  },
  {
    title: 'Headshot Generator',
    description: 'Create professional headshots with AI',
    uri: '/headshots',
    image: '/img/tools/headshots.png',
    slug: 'headshot-generator',
    seo_title: 'AI Headshot Generator - Create Professional Portraits',
    seo_description:
      'Create professional headshots and portraits with our AI headshot generator. Get studio-quality business photos without a photographer.',
    skipStaticPaths: true,
  },
  {
    title: 'Replace Background',
    description: 'Replace the background of your images with AI',
    uri: '/ai-images?action=replace-background',
    image: '/img/tools/replace-background.png',
    isNew: true,
    slug: 'replace-background',
    seo_title: 'AI Background Replacement - Change Image Backgrounds Instantly',
    seo_description:
      'Replace the background of your images with AI. Change photo backgrounds to any scene or setting instantly without complex editing.',
  },
  {
    title: 'Style Transfer',
    description: 'Transfer the style of one image to another',
    uri: '/ai-images?action=style-reference',
    image: '/img/tools/style-transfer.png',
    slug: 'style-transfer',
    seo_title: 'AI Style Transfer - Apply Artistic Styles to Your Images',
    seo_description:
      'Transfer the style of one image to another with our AI style transfer tool. Apply artistic styles and visual effects to transform your photos.',
  },
  {
    title: 'Caricature',
    description: 'Turn photos into fun, exaggerated caricatures',
    uri: '/ai-images?action=caricature',
    image: '/img/tools/caricature.png',
    slug: 'caricature',
    seo_title: 'Turn Photos into Fun Caricatures - AI Caricature Generator',
    seo_description:
      'Turn photos into fun caricatures with our AI caricature generator. Transform faces into artistic exaggerated cartoon versions.',
  },
  {
    title: 'Interior & Exterior Design',
    description: 'Create interior design images with AI',
    uri: '/ai-interior-designer',
    image: '/img/tools/interior-design.png',
    slug: 'interior-design',
    seo_title: 'AI Interior & Exterior Design - Visualize Home Transformations',
    seo_description:
      'Create stunning interior and exterior design visualizations with AI. Transform rooms and spaces with different styles and furniture instantly.',
    skipStaticPaths: true,
  },
  {
    title: 'Stylization',
    description: 'Create artistic stylized portraits from a single face image',
    uri: '/ai-images?action=stylization',
    image: '/img/tools/stylization.png',
    slug: 'stylization',
    seo_title: 'AI Portrait Stylization - Create Artistic Stylized Portraits',
    seo_description:
      'Create artistic stylized portraits from a single face image with our AI stylization tool. Transform photos into various artistic styles.',
  },
  {
    title: 'Train my AI Model',
    description: 'Create a custom AI model trained on your images',
    uri: '/ai-images/models',
    image: '/img/tools/train-model.png',
    isNew: true,
    slug: 'custom-ai-model',
    seo_title: 'Train Custom AI Image Models - Create Personalized AI Art',
    seo_description:
      'Create a custom AI model trained on your own images. Generate personalized AI art and designs with your unique style and subjects.',
    skipStaticPaths: true,
  },
  {
    title: 'Remove Background',
    description: 'Automatically remove backgrounds from your images',
    uri: '/ai-images?action=remove-background',
    image: '/img/tools/remove-background.webp',
    slug: 'remove-background',
    seo_title: 'AI Background Remover - Remove Image Backgrounds Instantly',
    seo_description:
      'Automatically remove backgrounds from your images with our AI tool. Create transparent background images for designs, products, and more.',
  },
  // Restore and enhance old or damaged photos by removing imperfections, adding color while preserving the original character and details of the image.
  {
    title: 'Colorize',
    description: 'Restore old or damaged photos',
    uri: '/ai-images?action=colorize',
    image: '/img/tools/restore-photo.png',
    slug: 'restore-photo',
    seo_title: 'AI Photo Colorizer - Restore Old or Damaged Photos',
    seo_description:
      'Restore old or damaged photos by removing imperfections, adding color while preserving the original character and details of the image.',
  },
  {
    title: 'Line Drawing',
    description: 'Transform photos into elegant line drawings',
    uri: '/ai-images?action=line-drawing',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e8d12d13-b24b-4a26-b92a-892ee97f142a.png',
    slug: 'line-drawing',
    seo_title: 'Turn Photos into Line Drawings - AI Line Art Generator',
    seo_description:
      'Turn your photos into elegant line drawings with our AI converter. Create beautiful line art from any image with a single click.',
  },
  {
    title: 'Oil Painting',
    description: 'Convert photos into artistic oil painting style',
    uri: '/ai-images?action=oil-painting',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/d2252f61-9d7a-4d78-a60c-c667b06578d5.png',
    slug: 'oil-painting',
    seo_title: 'Turn Photos into Oil Paintings - AI Oil Painting Generator',
    seo_description:
      'Turn your photos into stunning oil painting style artwork with our AI tool. Create beautiful artistic renditions of any image instantly.',
  },
  {
    title: 'Cartoon',
    description: 'Turn photos into fun cartoon style images',
    uri: '/ai-images?action=cartoon',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/c6455382-a86d-4753-be2f-eea3e46b7c15.png',
    slug: 'cartoon',
    seo_title: 'Turn Photos into Cartoons - AI Cartoon Style Generator',
    seo_description:
      'Turn your photos into fun cartoon style images with our AI cartoon converter. Create playful cartoon versions of any picture instantly.',
  },
  {
    title: 'Watercolor',
    description: 'Convert photos into beautiful watercolor art style',
    uri: '/ai-images?action=watercolor',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/31df5db5-ebf7-46bb-a9cd-1c980f7a9f55.png',
    slug: 'watercolor',
    seo_title: 'Turn Photos into Watercolor Art - AI Watercolor Generator',
    seo_description:
      'Turn your photos into beautiful watercolor style artwork with our AI tool. Create delicate, artistic watercolor renditions of any image.',
  },
  {
    title: 'Coloring Page',
    description: 'Turn photos into printable coloring pages',
    uri: '/ai-images?action=coloring-page',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/43fa1f5b-f862-47a8-8e29-b1b17b889e3c.png',
    slug: 'coloring-page',
    seo_title: 'Turn Photos into Coloring Pages - AI Coloring Page Generator',
    seo_description:
      'Turn your photos into printable coloring pages with our AI converter. Create custom coloring sheets from any image for kids and adults alike.',
  },
  {
    title: 'Sketch',
    description: 'Convert photos into artistic sketch style',
    uri: '/ai-images?action=sketch',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/a00d147c-b9bb-4313-833a-e3e2b2691737.png',
    slug: 'sketch',
    seo_title: 'Turn Photos into Sketches - AI Pencil Sketch Generator',
    seo_description:
      'Turn your photos into artistic sketch style with our AI tool. Create beautiful pencil-like sketches from any image with a single click.',
  },
];
